import { z } from "zod";

/**
 * A dashboard widget made by the assistant from a sentence (call of 04/11). It is a description,
 * never code: which read-only route of the API to read (with the viewer's own rights), which part
 * of the answer to show, and how. The screen draws it from these few known shapes.
 */
export const WIDGET_KINDS = ["metric", "table", "bar", "line"] as const;
export const WIDGET_FORMATS = ["text", "money", "number", "percent", "date"] as const;
export type WidgetFormat = (typeof WIDGET_FORMATS)[number];

/** A dotted path in the API's answer, e.g. "totals.netCents" or "items". Empty: the answer itself. */
const fieldPath = z
  .string()
  .trim()
  .max(80)
  .regex(/^[A-Za-z0-9_.]*$/, "errors.field.number");
const label = z.string().trim().min(1).max(60);

export const widgetFieldSchema = z.object({
  field: fieldPath,
  label,
  /** Amounts in cents are shown as money; ratios (0.42) as percent. */
  format: z.enum(WIDGET_FORMATS).default("text"),
});
export type WidgetField = z.output<typeof widgetFieldSchema>;

export const customWidgetSchema = z.object({
  title: z.string().trim().min(1).max(80),
  kind: z.enum(WIDGET_KINDS),
  source: z.object({
    /** A GET route of the API, without /api (e.g. "/invoices"), ids included. */
    path: z
      .string()
      .trim()
      .max(200)
      .regex(/^\/[A-Za-z0-9/_-]*$/, "errors.field.number"),
    query: z.record(z.string().max(40), z.string().max(200)).default({}),
  }),
  /** Where the rows are in the answer ("items", "rows"…); empty when the answer is the list. */
  itemsPath: fieldPath.default(""),
  /** metric: a value of the answer, or an aggregate of the rows. */
  value: z
    .object({
      path: fieldPath.optional(),
      aggregate: z.enum(["count", "sum"]).optional(),
      field: fieldPath.optional(),
      format: z.enum(WIDGET_FORMATS).default("number"),
    })
    .optional(),
  /** table: the columns shown. */
  columns: z.array(widgetFieldSchema).max(8).default([]),
  /** bar / line: the category (or date) and up to three values. */
  x: widgetFieldSchema.optional(),
  series: z.array(widgetFieldSchema).max(3).default([]),
  /** A row opens this page of the ERP, "{field}" replaced by the row's value (e.g. "/orders/{id}"). */
  link: z
    .string()
    .trim()
    .max(120)
    .regex(/^\/[A-Za-z0-9/_{}.-]*$/, "errors.field.number")
    .optional(),
  limit: z.number().int().min(1).max(50).default(10),
});
export type CustomWidget = z.output<typeof customWidgetSchema>;

export const widgetPromptSchema = z.object({ prompt: z.string().trim().min(3, "errors.field.required").max(1000) });
export type WidgetPromptInput = z.input<typeof widgetPromptSchema>;

/** A value at a dotted path of an object ("totals.netCents"); the object itself for "". */
export function pick(value: unknown, path: string | undefined): unknown {
  if (!path) return value;
  let current: unknown = value;
  for (const part of path.split(".")) {
    if (current === null || typeof current !== "object") return undefined;
    current = (current as Record<string, unknown>)[part];
  }
  return current;
}
